import { defineEvent, withPii } from "@amtana/module";
import { z } from "zod";

import { membershipRoleSchema } from "./access-requests.schemas.ts";

// Access requests' facts (ADR-022, epic 26, S26.03/S26.13): one stream per request,
// `access-requests:<accessRequestId>`, tenanted by the organization asked to
// join. A fact carries ids and names a person may see, nothing more.
//
// `access_requested` carries its own `adminUserIds` (D490/D511's own shape: the router
// cannot peer organization for a live read, so whichever module already safely peers it
// resolves and carries what the delivery needs), resolved through access-requests' own
// existing `organization` peer, beside `organizationName`, `requesterName` and
// `requesterEmail` the settings-aware "access-request-received" notice renders. The two
// decisions carry `organizationName` the same way, for their own notice to the requester.
//
// `organizationName` is marked `.meta({ piiFree: true })` (review group 3 security finding
// 11): an organization's own name, never a person's, the one field here the repo-wide PII
// heuristic's own `name$` regex would otherwise flag.

export const ACCESS_REQUESTS = "access-requests";

export const accessRequested = withPii(
  defineEvent(
    ACCESS_REQUESTS,
    "access_requested",
    z.object({
      accessRequestId: z.string(),
      organizationId: z.string(),
      organizationName: z.string().meta({ piiFree: true }),
      userId: z.string(),
      adminUserIds: z.array(z.string()),
      requesterName: z.string(),
      requesterEmail: z.string(),
    }),
  ),
  ["requesterName", "requesterEmail"],
);

export const accessApproved = defineEvent(
  ACCESS_REQUESTS,
  "access_approved",
  z.object({
    accessRequestId: z.string(),
    organizationId: z.string(),
    organizationName: z.string().meta({ piiFree: true }),
    userId: z.string(),
    role: membershipRoleSchema,
    decidedByUserId: z.string(),
  }),
);

export const accessDeclined = defineEvent(
  ACCESS_REQUESTS,
  "access_declined",
  z.object({
    accessRequestId: z.string(),
    organizationId: z.string(),
    organizationName: z.string().meta({ piiFree: true }),
    userId: z.string(),
    decidedByUserId: z.string(),
  }),
);

export const accessRequestEvents = [accessRequested, accessApproved, accessDeclined] as const;
